import {AfterViewInit, Component, ElementRef, HostListener, Input, OnInit, ViewChild} from '@angular/core';
import {Order} from '../../../model/order';
import {ModalConfig} from '../../../model/modal-config';
import {EmitterConstants} from '../../../shared/emitter-constants';
import {Emitter} from '../../../shared/utils/emitter/emitter';
import {PageNames} from '../../../shared/page-names.enum';
import {NavigationService} from '../../../services/navigation/navigation.service';
import {BaseActions} from '../../../shared/base-actions';
import {takeUntil} from 'rxjs/operators';
import {OcapiService} from '../../../services/ocapi/ocapi.service';
import {TrialSalesOffersMapper} from '../../../shared/mappers/trial/trial-sales-offers-mapper';
import {TrialSalesOffer} from '../../../model/ocapi/trial-sales-offer';
import {Trial} from '../../../model/trial';
import {ErrorMessageConstants} from '../../../shared/error-message-constants';
import {AnalyticsService} from '../../../services/analytics/analytics.service';
import {KeyDirections} from '../../../shared/key-directions.enum';
import {TvshopConstants} from '../../../shared/tvshop-constants';
import {Error} from '../../../model/error';
import {LanguageService} from '../../../services/language/language.service';
import {TrialUtils} from '../../../shared/utils/trial/trial-utils';
import {DtvCatalogUtils} from '../../../utils/dtv-catalog/dtv-catalog.utils';
import {OmapiIdsUtils} from '../../../shared/utils/omapi-ids/omapi-ids-utils';
import {UserTypeConstants} from '../../../shared/user-type-constants';
import {OcapiSynchronousService} from '../../../services/ocapi/ocapi.synchronous.service';
import {OcapiAsynchronousService} from '../../../services/ocapi/ocapi.asynchronous.service';
import {TvShopStateService} from '../../../services/state/tv-shop-state.service';

@Component({
  selector: 'app-trial-code-modal',
  templateUrl: './trial-code-modal.component.html',
  styleUrls: ['./trial-code-modal.component.less']
})
export class TrialCodeModalComponent extends BaseActions implements AfterViewInit, OnInit {
  @Input() order: Order;
  @Input() titleKey: string;
  @Input() descriptionKey: string;
  @Input() config: ModalConfig;
  @Input() code: string;
  @Input() runningTrials = [];
  @ViewChild('conditions', {read: ElementRef}) conditions: ElementRef;
  @ViewChild('confirm', {read: ElementRef}) confirm: ElementRef;
  @ViewChild('modalHeader', {read: ElementRef}) modalHeader: ElementRef;

  isCodeValid = true;
  ownerComponent = PageNames.TRIAL_CODE_MODAL;
  voucherErrorCode = ErrorMessageConstants.VOUCHER_NOT_VALID;
  attemptsRemaining: string;
  elementArray: Array<ElementRef> = [];
  focussedElementIndex = 1;
  unavailableReasons = new Set();
  trialAlreadyExists = false;
  private availableSalesOffers = [];
  chosenTrial: Trial = undefined;

  constructor(private navigationService: NavigationService,
              private ocapiService: OcapiService,
              private ocapiSynchronousService: OcapiSynchronousService,
              private ocapiAsynchronousService: OcapiAsynchronousService,
              private languageService: LanguageService,
              private stateService: TvShopStateService,
              private analyticsService: AnalyticsService) {
    super();
  }

  ngOnInit(): void {
    this.doneLoading = true;
    Emitter.of(EmitterConstants.INPUT_TRIAL_CODE).pipe(takeUntil(this.stopSubscription)).subscribe(value => {
      this.code = value.code;
      this.isCodeValid = true;
    });
    this.elementArray.push(this.modalHeader);
    this.elementArray.push(this.confirm);
    this.elementArray.push(this.conditions);
  }

  public retrieveTrialSalesOffers(): void {
    this.unavailableReasons = new Set<any>();
    this.isCodeValid = !!this.code;
    this.doneLoading = false;
    if (this.code) {
      this.ocapiService.retrieveTrialOffers(this.code).pipe(takeUntil(this.stopSubscription)).subscribe(
        (trialSalesOffers: Array<TrialSalesOffer>) => {
          this.handleTrialSalesOffer(trialSalesOffers);
        }, error => {
          this.handleTrialErrorResponse(error);
        }
      );
    } else {
      this.doneLoading = true;
      this.voucherErrorCode = ErrorMessageConstants.VOUCHER_NOT_VALID;
      this.attemptsRemaining = undefined;
    }
    this.activateTimeout();
  }

  private handleTrialSalesOffer(trialSalesOffers: Array<TrialSalesOffer>) {
    const thereAreValidTrials = this.validateTrialSalesOffers(trialSalesOffers);
    if (thereAreValidTrials) {
      if (trialSalesOffers && trialSalesOffers.length === 1) {
        const trial = TrialSalesOffersMapper.mapToTrial(trialSalesOffers[0], this.code);
        this.analyticsService.onVoucherCodeResolved([trial.omapiId]);
        this.activateTrial(trialSalesOffers);
      } else if (trialSalesOffers && trialSalesOffers.length > 1) {
        this.analyticsService.onVoucherCodeResolved(trialSalesOffers.map(value => TrialSalesOffersMapper.mapToTrial(value, this.code).omapiId));
        this.openComparisonModal(trialSalesOffers);
      }
    } else {
      this.voucherErrorCode = this.trialAlreadyExists ? this.voucherErrorCode : ErrorMessageConstants.VOUCHER_NOT_VALID;
      this.analyticsService.onVoucherCodeFailed();
      this.logInvalidTrialSalesOffers();
    }
  }

  private handleTrialErrorResponse(error) {
    this.containsErrors('An error has been encountered while retrieving the TrialsSalesOffers', error);
    this.attemptsRemaining = undefined;
    this.analyticsService.onVoucherCodeFailed();
    this.isCodeValid = false;
    this.mapVoucherErrorCodes(error.error ? error.error.code : ErrorMessageConstants.VOUCHER_NOT_VALID);
    if (this.voucherErrorCode === ErrorMessageConstants.MAX_ATTEMPTS_REACHED) {
      this.doneLoading = true;
      Emitter.of(EmitterConstants.OPEN_TRIAL_MAX_ATTEMPTS_REACHED_MODAL).emit();
    } else {
      this.logInvalidTrialSalesOffers();
    }
  }

  private logErrorNoTrialSalesOfferFound() {
    this.isCodeValid = false;
    this.containsErrors('No TrialSalesOffer has been found for this trial', undefined);
  }

  private logInvalidTrialSalesOffers() {
    this.isCodeValid = false;
    this.doneLoading = true;
    this.containsErrors('TrialSalesOffers are not valid', undefined);
  }

  private activateTrial(trialSalesOffers) {
    this.doneLoading = false;
    this.chosenTrial = TrialSalesOffersMapper.mapToTrial(trialSalesOffers[0], this.code);
    this.analyticsService.onVoucherCodeEntered();
    let activateTrialObservable;

    if (OmapiIdsUtils.currentUserType === UserTypeConstants.LEGACY_USER) {
      activateTrialObservable = this.ocapiAsynchronousService.activateTrial(this.chosenTrial);
    } else if (OmapiIdsUtils.currentUserType === UserTypeConstants.DWN_USER) {
      activateTrialObservable = this.ocapiSynchronousService.activateTrial(this.chosenTrial);
    }

    activateTrialObservable.pipe(takeUntil(this.stopSubscription))
      .subscribe(
        response => {
          Emitter.of(EmitterConstants.OPEN_TRIAL_SUCCESS_MODAL).emit(this.chosenTrial);
          this.doneLoading = true;
          const packs = this.stateService.selectSnapshot(state => state.tvShopEntertainmentPacks);
          DtvCatalogUtils.onTrialActivated(packs, this.chosenTrial);
          this.stateService.setTvShopEntertainmentPacks(packs);
        }, error1 => this.containsErrors('Trial could not be activated', error1));
  }

  private openComparisonModal(trialSalesOffers) {
    this.doneLoading = true;
    Emitter.of(EmitterConstants.OPEN_TRIAL_COMPARISON_MODAL).emit(TrialSalesOffersMapper.mapToTrials(trialSalesOffers, this.code));
  }

  public onBack(): void {
    (document.activeElement as HTMLElement).blur();
    this.doneLoading = true;
    Emitter.of(EmitterConstants.BACK_PRESSED).emit(this.ownerComponent);
  }

  public openConditional(): void {
    this.doneLoading = true;
    Emitter.of(EmitterConstants.OPEN_CONDITIONAL_MODAL).emit(this.ownerComponent);
  }

  public openTrialActivationTimeout(): void {
    const pendingError = new Error('timeout', 'Your order is pending but there is currently no response.');
    Emitter.of(EmitterConstants.OPEN_TRIAL_ACTIVATION_TIMEOUT).emit({
      trial: this.chosenTrial,
      component: this.ownerComponent,
      error: pendingError
    });
  }

  ngAfterViewInit(): void {
    this.navigationService.resetNavigation();
  }

  private validateTrialSalesOffers(trialSalesOffers: Array<TrialSalesOffer>): boolean {
    let thereAreValidTrials = false;
    this.availableSalesOffers = [];
    if (trialSalesOffers && trialSalesOffers.length > 0) {
      this.enrichAvailableSalesOffersAndUnavailableReasons(trialSalesOffers);
      const isSalesOffer1Valid = trialSalesOffers[0].voucherisvalid && this.availableSalesOffers[0];
      const isSalesOffer1And2Valid = isSalesOffer1Valid && (trialSalesOffers.length > 1 ? trialSalesOffers[1].voucherisvalid && this.availableSalesOffers[1] : true);
      this.attemptsRemaining = '' + trialSalesOffers[0].attemptsremaining;
      thereAreValidTrials = this.isCodeValid = trialSalesOffers.length === 1 ? isSalesOffer1Valid : isSalesOffer1And2Valid;
      if (isSalesOffer1And2Valid && this.trialSalesOfferAlreadyExists(trialSalesOffers)) {
        this.voucherErrorCode = ErrorMessageConstants.TRIAL_ALREADY_RUNNING;
        this.trialAlreadyExists = true;
        this.containsErrors('You already have this trial', 'Already Subscribed!');
        return this.isCodeValid = false;
      }
      this.voucherErrorCode = trialSalesOffers[0].voucherisvalid ? this.voucherErrorCode : ErrorMessageConstants.VOUCHER_NOT_VALID;
    } else {
      this.analyticsService.onVoucherCodeFailed();
      this.logErrorNoTrialSalesOfferFound();
      thereAreValidTrials = false;
    }
    return thereAreValidTrials;
  }

  private enrichAvailableSalesOffersAndUnavailableReasons(trialSalesOffers: Array<TrialSalesOffer>): void {
    this.enrichtAvailableSalesOffers(trialSalesOffers);
    this.enrichUnavailableReasons(trialSalesOffers);
  }

  private enrichUnavailableReasons(trialSalesOffers: Array<TrialSalesOffer>) {
    trialSalesOffers.filter(trialSalesOffer => !trialSalesOffer.salesofferisavailable
      && trialSalesOffer.unavailablereasons
      && Object.entries(trialSalesOffer.unavailablereasons).length > 0).forEach(trialSalesOffer => {
      const currentLanguage = (this.languageService.getCurrentLanguage() + '').toUpperCase();
      let unavailableReason = trialSalesOffer.unavailablereasons[currentLanguage] + ''.trim();
      unavailableReason = unavailableReason.endsWith('.') ? unavailableReason :
        unavailableReason.endsWith('!') ? unavailableReason : unavailableReason.concat('.');
      this.unavailableReasons.add(unavailableReason);
    });
  }

  private enrichtAvailableSalesOffers(trialSalesOffers: Array<TrialSalesOffer>) {
    trialSalesOffers.forEach(trialSalesOffer => this.availableSalesOffers.push(trialSalesOffer.salesofferisavailable));
  }

  private mapVoucherErrorCodes(errorCode: string): void {
    this.voucherErrorCode = ErrorMessageConstants.OCAPI_ERROR_MAP.get(errorCode) || ErrorMessageConstants.VOUCHER_GENERIC_ERROR;
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.keyCode === KeyDirections.ARROW_DOWN && this.focussedElementIndex < this.elementArray.length - 1) {
      this.focussedElementIndex++;
      this.elementArray[this.focussedElementIndex].nativeElement.scrollIntoViewIfNeeded();
    }
    if (event.keyCode === KeyDirections.ARROW_UP && this.focussedElementIndex > 0) {
      this.focussedElementIndex--;
      this.elementArray[this.focussedElementIndex].nativeElement.scrollIntoViewIfNeeded();
    }
  }

  private activateTimeout(): void {
    setTimeout(() => {
      if (!this.doneLoading) {
        this.doneLoading = true;
        this.openTrialActivationTimeout();
        this.containsErrors('The order is pending.');
      }
    }, TvshopConstants.MAXIMUM_ORDERING_COOL_DOWN_IN_MS);
  }

  private trialSalesOfferAlreadyExists(trialSalesOffers: Array<TrialSalesOffer>): boolean {
    if (this.runningTrials.length > 0) {
      for (const trialSalesOffer of trialSalesOffers) {
        if (this.trialWithOmapiIdAlreadyExists(trialSalesOffer)) {
          this.attemptsRemaining = undefined;
          return true;
        }
      }
    }
    return false;
  }

  private trialWithOmapiIdAlreadyExists(trialSalesOffer: TrialSalesOffer): boolean {
    const omapiId = TrialUtils.getTrialOmapiIdForTrialLabel(trialSalesOffer.labelkey);
    return !!this.runningTrials.find(runningTrial => runningTrial.omapiId === omapiId);
  }

}
